import { QueryClient } from '@tanstack/react-query';
import { GetServerSideProps } from 'next';
import { Session } from 'next-auth';
import { getSession } from 'next-auth/react';
import styled from 'styled-components';

import LandingPageHome from '../components/LandingPageHome';
import { NextPageWithLayout } from '../components/layouts';
import LandingPageLayout from '../components/layouts/LandingPageLayout';
import useRoutePrefetch from '../hooks/useRoutePrefetch';
import { DefaultLocale } from '../providers/tolgee';
import CoreAPIService from '../services/CoreAPIService';
import { detectAndBestEffortSetLanguage } from '../utils/localisationUtils';
import { dehydrateReactQueryPrefetchedInfiniteQuery } from '../utils/react-query-utils';
import { provisionFlags } from '../utils/ServerSideFeatureFlagUtils';

const Home: NextPageWithLayout = () => {
  useRoutePrefetch('/[tenantId]/home');
  useRoutePrefetch('/[tenantId]/lessons');
  useRoutePrefetch('/[tenantId]/workspace/[workspaceId]');

  return (
    <Container>
      <LandingPageLayout
        title="Curipod - Interactive Lessons for K-12 that Students Love"
        subTitle="Experience the joy of every student participating, engaging, and learning with instant lessons for every subject."
        locale={DefaultLocale}
        hideBlobs={true}
        disableHeadline={true}
      >
        <LandingPageHome />
      </LandingPageLayout>
    </Container>
  );
};

const Container = styled.div`
  min-height: 100vh;
`;

const TIMEOUT = 20000;
export const getServerSideProps: GetServerSideProps = async context => {
  try {
    const queryClient = new QueryClient();
    const sessionPromise = getSession({ req: context.req });
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Timeout')), TIMEOUT),
    );

    const session = (await Promise.race([
      sessionPromise,
      timeoutPromise,
    ])) as Session | null;

    if (session) {
      const { activeTenantId } = await CoreAPIService.fetchActiveTenantId(
        session.user.uid,
      );

      const flags = await provisionFlags(context, queryClient, session);
      const isPersonalizedHomePageEnabled = flags.unleash.toggles?.find(
        toggle => toggle.name === 'personalized_create_page',
      )?.enabled;
      if (isPersonalizedHomePageEnabled) {
        return {
          redirect: {
            permanent: false,
            destination: `/${activeTenantId}/home`,
          },
        };
      }

      return {
        redirect: {
          permanent: false,
          destination: `/${activeTenantId}/lessons`,
        },
      };
    } else {
      context.res.setHeader(
        'Cache-Control',
        'public, s-maxage=3600, stale-while-revalidate=1209600',
      );
      const queryClient = new QueryClient();

      return {
        props: {
          dehydratedState: dehydrateReactQueryPrefetchedInfiniteQuery(queryClient),
          ...(await detectAndBestEffortSetLanguage(context)),
        },
      };
    }
  } catch (error) {
    return {
      props: {
        ...(await detectAndBestEffortSetLanguage(context)),
      },
    };
  }
};

export default Home;
